import React, { useState, useEffect, useMemo, useReducer, useContext } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";

import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";
import WebhookModal from "../../components/WebhookModal";
import {
  AddCircle,
  Build,
  ContentCopy,
  DevicesFold,
  MoreVert,
  WebhookOutlined
} from "@mui/icons-material";

import {
  Button,
  CircularProgress,
  Chip,
  Grid,
  Menu,
  MenuItem,
  Stack
} from "@mui/material";

import FlowBuilderModal from "../../components/FlowBuilderModal";

import {
  colorBackgroundTable,
  colorLineTable,
  colorLineTableHover,
  colorPrimary,
  colorTitleTable,
  colorTopTable
} from "../../styles/styles";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach(contact => {
      const contactIndex = state.findIndex(c => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex(c => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex(c => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    backgroundColor: colorBackgroundTable(),
    borderRadius: 12,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  }
}));

const FlowBuilder = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);

  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder/list");
          setWebhooks(data?.flows || []);
          dispatch({ type: "LOAD_CONTACTS", payload: data?.flows || [] });
          setHasMore(data?.hasMore || false);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadData]);

  useEffect(() => {
    const companyId = user.companyId;

    const onContact = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    };

    socket.on(`company-${companyId}-contact`, onContact);

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleSearch = event => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const filteredWebhooks = useMemo(() => {
    const q = String(searchParam || "").trim().toLowerCase();
    if (!q) return Array.isArray(webhooks) ? webhooks : [];
    return (Array.isArray(webhooks) ? webhooks : []).filter((w) =>
      String(w?.name || "").toLowerCase().includes(q)
    );
  }, [searchParam, webhooks]);

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const handleCloseOrOpenTicket = ticket => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = () => {
    setSelectedContactId(deletingContact.id);
    setSelectedWebhookName(deletingContact.name);
    setContactModalOpen(true);
  };

  const handleDeleteWebhook = async webhookId => {
    try {
      await api.delete(`/flowbuilder/delete/${webhookId}`).then(res => {
        setDeletingContact(null);
        setReloadData(old => !old);
      });
      toast.success(i18n.t("flowBuilder.list.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async flowId => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId: flowId }).then(res => {
        setDeletingContact(null);
        setReloadData(old => !old);
      });
      toast.success(i18n.t("flowBuilder.list.toasts.duplicated"));
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1);
  };

  const handleScroll = e => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportLink = () => {
    history.push(`/flowbuilder/${deletingContact.id}/canvas`);
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={ticket => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <FlowBuilderModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName}
        onSave={() => setReloadData(old => !old)}
      />
      <ConfirmationModal
        title={i18n.t("flowBuilder.list.confirm.deleteTitle", {
          name: deletingContact?.name || "",
        })}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={e =>
          deletingContact ? handleDeleteWebhook(deletingContact.id) : () => {}
        }
      >
        {i18n.t("flowBuilder.list.confirm.deleteBody")}
      </ConfirmationModal>
      <ConfirmationModal
        title={i18n.t("flowBuilder.list.confirm.duplicateTitle", {
          name: deletingContact?.name || "",
        })}
        open={confirmDuplicateOpen}
        onClose={setConfirmDuplicateOpen}
        onConfirm={e =>
          deletingContact ? handleDuplicateFlow(deletingContact.id) : () => {}
        }
      >
        {i18n.t("flowBuilder.list.confirm.duplicateBody")}
      </ConfirmationModal>
      <MainHeader>
        <Title data-testid="flowbuilder-title">{i18n.t("flowBuilder.list.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("flowBuilder.list.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            variant="outlined"
            size="small"
            InputProps={{
              style: {
                color: colorTitleTable(),
                borderRadius: 12,
                background: "rgba(255,255,255,0.04)"
              },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            onClick={handleOpenContactModal}
            style={{
              textTransform: "none",
              backgroundColor: theme.palette.primary.main,
              color: "#fff"
            }}
          >
            <Stack direction={"row"} gap={1}>
              <AddCircle />
              {i18n.t("flowBuilder.list.addFlow")}
            </Stack>
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
        onScroll={handleScroll}
      >
        <Stack spacing={1.25} sx={{ padding: 1.25 }}>
          {!loading && filteredWebhooks.length === 0 && (
            <Stack
              justifyContent="center"
              alignItems="center"
              spacing={1}
              sx={{
                minHeight: "40vh",
                textAlign: "center",
                color: colorTopTable(),
                borderRadius: 3,
                border: "1px dashed rgba(255,255,255,0.16)",
                background: "rgba(255,255,255,0.03)",
              }}
            >
              <DevicesFold sx={{ width: 44, height: 44, color: "rgba(255,255,255,0.55)" }} />
              <div style={{ fontWeight: 700, color: colorTitleTable() }}>
                {searchParam ? i18n.t("flowBuilder.list.noMatches") : i18n.t("flowBuilder.list.empty")}
              </div>
            </Stack>
          )}

          {filteredWebhooks?.map((contact) => (
            <Grid
              container
              key={contact.id}
              onClick={() => history.push(`/flowbuilder/${contact.id}/canvas`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  history.push(`/flowbuilder/${contact.id}/canvas`);
                }
              }}
              sx={{
                padding: "12px",
                borderRadius: 3,
                cursor: "pointer",
                border: "1px solid rgba(255,255,255,0.10)",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.03))",
                transition: "transform 120ms ease, background 120ms ease, border-color 120ms ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  borderColor: "rgba(0,0,255,0.35)",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.035))",
                },
              }}
            >
              <Grid item xs={12} sm={7} md={7}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      background: "rgba(0,0,255,0.18)",
                      border: "1px solid rgba(0,0,255,0.35)",
                      flex: "0 0 auto",
                    }}
                  >
                    <DevicesFold sx={{ width: 20, height: 20, color: "#e5e5e5" }} />
                  </Stack>

                  <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                    <div
                      style={{
                        color: colorTitleTable(),
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {contact.name}
                    </div>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="small"
                        variant="outlined"
                        label={
                          contact.active
                            ? i18n.t("flowBuilder.list.status.active")
                            : i18n.t("flowBuilder.list.status.inactive")
                        }
                        sx={{
                          height: 24,
                          fontWeight: 600,
                          borderColor: contact.active ? "rgba(16,185,129,0.65)" : "rgba(148,163,184,0.55)",
                          color: contact.active ? "rgb(167,243,208)" : "rgb(226,232,240)",
                          backgroundColor: contact.active ? "rgba(16,185,129,0.10)" : "rgba(148,163,184,0.10)",
                        }}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={5} md={5}>
                <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
                  <Button
                    id="basic-button"
                    aria-controls={open ? "basic-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick(e);
                      setDeletingContact(contact);
                    }}
                    sx={{
                      borderRadius: "36px",
                      minWidth: "42px",
                      height: "36px",
                      border: "1px solid rgba(255,255,255,0.14)",
                    }}
                  >
                    <MoreVert sx={{ color: "#ededed", width: 22, height: 22 }} />
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          ))}

          <Menu
            id="basic-menu"
            anchorEl={anchorEl}
            open={open}
            sx={{ borderRadius: "40px" }}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "basic-button",
            }}
          >
            <MenuItem
              onClick={() => {
                handleClose();
                hadleEditContact();
              }}
            >
              {i18n.t("flowBuilder.list.menu.editName")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                exportLink();
              }}
            >
              {i18n.t("flowBuilder.list.menu.editFlow")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                setConfirmDuplicateOpen(true);
              }}
            >
              {i18n.t("flowBuilder.list.menu.duplicate")}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                setConfirmOpen(true);
              }}
            >
              {i18n.t("flowBuilder.list.menu.delete")}
            </MenuItem>
          </Menu>

          {loading && (
            <Stack justifyContent={"center"} alignItems={"center"} minHeight={"40vh"}>
              <CircularProgress />
            </Stack>
          )}
        </Stack>
      </Paper>
    </MainContainer>
  );
};

export default FlowBuilder;
